import { v4 as uuidv4 } from 'uuid';
import { centsToMoney, formatLocaleDate, moneyToCents } from '../../utils';

export const dateFormat = 'yyyy-MM-dd';

export const RewardTypes = {
  VIRTUAL: 201,
  DISCOUNT: 202,
  MEMBERSHIP: 203,
};

export const mapRewardType = type => {
  switch (type) {
    case 201:
      return 'Virtualūs pinigai';
    case 202:
      return 'Nuolaida';
    case 203:
      return 'Planas';
    default:
      return '-';
  }
};

export const generateGoalValues = step => ({
  id: uuidv4(),
  step: step || 1,
  amount_goal: 10,
  type: 101,
  service_type: '',
  wallet_reward: 10,
  reward_type: RewardTypes.VIRTUAL,
  reward_service_types: [],
  coupon_amount: 0,
  discount_percent: 0,
  membership_id: '',
  discount_valid_days: 0,
});

export const loyaltyProgramFormDefaultValues = {
  name: '',
  valid_from: '',
  valid_to: '',
  period: 63,
  description_lt: '',
  description_en: '',
  description_ru: '',
  goals: [generateGoalValues(1)],
};

const moneyToCentsSafe = value => {
  if (value === '' || value === undefined || value === null) return undefined;
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return undefined;
  return moneyToCents(normalized);
};

const centsToMoneySafe = value => {
  if (value === '' || value === undefined || value === null) return 0;
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return 0;
  return centsToMoney(normalized);
};

/**
 * Maps form goal to API payload.
 * Omits fields not needed for the goal's type/reward_type.
 * Backend requires: unused fields = null or omitted, amount_goal > 0.
 */
export const mapGoalToApiPayload = goal => {
  const { type, reward_type } = goal;
  const amountGoalCents = moneyToCentsSafe(goal.amount_goal);

  const payload = {
    step: goal.step,
    amount_goal: Math.max(1, amountGoalCents || 1),
    type,
    reward_type,
  };

  if (type === 102) {
    const serviceType = goal.service_type;
    if (
      serviceType !== '' &&
      serviceType !== undefined &&
      serviceType !== null
    ) {
      payload.service_type = Number(serviceType);
    }
  }

  if (reward_type === RewardTypes.VIRTUAL) {
    const walletRewardCents = moneyToCentsSafe(goal.wallet_reward);
    if (walletRewardCents !== undefined && walletRewardCents !== null) {
      payload.wallet_reward = walletRewardCents;
    }
  }

  if (reward_type === RewardTypes.DISCOUNT) {
    const discountPercent = goal.discount_percent;
    if (
      discountPercent !== '' &&
      discountPercent !== undefined &&
      discountPercent !== null
    ) {
      payload.discount_percent = Number(discountPercent);
    }
    const serviceTypes = goal.reward_service_types;
    if (
      serviceTypes &&
      (Array.isArray(serviceTypes) ? serviceTypes.length > 0 : serviceTypes)
    ) {
      payload.reward_service_types = Array.isArray(serviceTypes)
        ? serviceTypes.map(Number)
        : [Number(serviceTypes)];
    }
  }

  if (reward_type === RewardTypes.MEMBERSHIP) {
    const membershipId = goal.membership_id;
    if (
      membershipId !== '' &&
      membershipId !== undefined &&
      membershipId !== null
    ) {
      payload.membership_id = Number(membershipId);
    }
    const validDays = goal.discount_valid_days;
    if (validDays !== '' && validDays !== undefined && validDays !== null) {
      payload.discount_valid_days = Number(validDays);
    }
  }

  return payload;
};

export const loyaltyProgramDefaultValuesMapper = program => {
  if (!program) return loyaltyProgramFormDefaultValues;

  const mapGoal = (goal, index) => ({
    id: goal.id ?? uuidv4(),
    step: goal.step ?? index + 1,
    amount_goal: centsToMoneySafe(goal.amount_goal ?? 1000),
    type: goal.type ?? 101,
    service_type: goal.service_type ?? '',
    wallet_reward: centsToMoneySafe(goal.wallet_reward ?? 0),
    reward_type: goal.reward_type ?? RewardTypes.VIRTUAL,
    reward_service_types: goal.reward_service_types ?? '',
    discount_percent: goal.discount_percent ?? 0,
    membership_id: goal.membership_id ?? '',
    discount_valid_days: goal.discount_valid_days ?? 0,
  });

  return {
    name: program.name ?? '',
    valid_from: program.valid_from
      ? formatLocaleDate(program.valid_from, 'yyyy-MM-dd')
      : '',
    valid_to: program.valid_to
      ? formatLocaleDate(program.valid_to, 'yyyy-MM-dd')
      : '',
    period: program.period ?? 63,
    description_lt: program.description_lt ?? '',
    description_en: program.description_en ?? '',
    description_ru: program.description_ru ?? '',
    goals: Array.isArray(program.goals)
      ? program.goals.map(mapGoal)
      : [generateGoalValues(1)],
  };
};
